import { useEffect, useState, useMemo } from "react";
import { useAtom } from "jotai";
import { useController } from "react-hook-form";
import debounce from "lodash.debounce";
import { useTransferPricing } from "@design-lab/entities/transfers-lab/api";
import type { Lens } from "@hookform/lenses";
import { transferPricingAtom, pricingLoadingAtom, createPricingKey } from "../model/pricingAtoms";

interface UsePresetSizePricingParams {
  artworkId: string;
  transferTypeId: string;
  sizeId: string;
  quantityLens: Lens<number>;
  width: number;
  height: number;
  enhancementTier: string;
  enabled?: boolean;
}

/**
 * Hook to fetch DTF pricing for a preset size (fixed dimensions, variable quantity)
 */
export function usePresetSizePricing({
  artworkId,
  transferTypeId,
  sizeId,
  quantityLens,
  width,
  height,
  enhancementTier,
  enabled = true
}: UsePresetSizePricingParams) {
  // Get quantity value through lens
  const { field: quantityField } = useController(quantityLens.interop());
  const currentQuantity = quantityField.value ?? 0;

  // State for debounced values
  const [debouncedQuantity, setDebouncedQuantity] = useState(currentQuantity);
  const [isDebouncing, setIsDebouncing] = useState(false);

  // Create debounced update function
  const debouncedUpdate = useMemo(
    () =>
      debounce((quantity: number) => {
        setDebouncedQuantity(quantity);
        setIsDebouncing(false);
      }, 500),
    []
  );

  // Update debounced value when quantity changes
  useEffect(() => {
    if (currentQuantity !== debouncedQuantity) {
      setIsDebouncing(true);
      debouncedUpdate(currentQuantity);
    }
  }, [currentQuantity, debouncedQuantity, debouncedUpdate]);

  // Cleanup debounce on unmount
  useEffect(() => () => debouncedUpdate.cancel(), [debouncedUpdate]);

  // Get the main atoms
  const [allPricing, setAllPricing] = useAtom(transferPricingAtom);
  const [, setAllLoading] = useAtom(pricingLoadingAtom);

  // Create stable key for this size
  const pricingKey = createPricingKey(artworkId, transferTypeId, sizeId);

  // Create a unique ID for this specific preset size pricing
  // Remove hyphens to match backend's expected format
  const pricingId = `${artworkId}${sizeId}`.replace(/-/g, "");

  // Fetch pricing when debounced quantity changes
  const {
    data: pricing,
    isLoading: isFetching,
    error
  } = useTransferPricing({
    transferTypeId,
    artworkId: pricingId,
    width,
    height,
    quantity: debouncedQuantity,
    enhancementTier,
    hasExpertEditAddon: false,
    enabled: enabled && !!transferTypeId && width > 0 && height > 0 && debouncedQuantity > 0
  });

  // Show loading state during debouncing or fetching
  const isLoading = isDebouncing || isFetching;

  // Update pricing atom when pricing changes
  useEffect(() => {
    if (pricing) {
      setAllPricing(prev => ({
        ...prev,
        [pricingKey]: pricing
      }));
    }
  }, [pricing, pricingKey, setAllPricing]);

  // Update loading state
  useEffect(() => {
    setAllLoading(prev => ({
      ...prev,
      [pricingKey]: isLoading
    }));

    return () => {
      setAllLoading(prev => {
        if (!(pricingKey in prev)) {
          return prev;
        }
        const next = { ...prev };
        delete next[pricingKey];
        return next;
      });
    };
  }, [isLoading, pricingKey, setAllLoading]);

  // Return fresh data if available, otherwise stored data
  const currentPricing = pricing || allPricing[pricingKey] || null;

  return {
    pricing: currentPricing,
    isLoading,
    error
  };
}
